import { Chip } from '@vnetwork/ui/primitives';
import type { OpsOrgDetail } from '@/api/operations-org-hooks';
import type { ImpersonationSession } from '@/lib/impersonation-session';
import { DetailHeader } from '@/components/detail-header';

/**
 * Org detail identity header — breadcrumbs + h1 row.
 * Pattern: Breadcrumbs → h1 (org ID muted prefix + customer name) + category chips.
 * When a customer token is active a subtle "Viewing as customer" chip is shown
 * in the header — no amber banner, no TTL countdown, no Exit button.
 */
export function OpsOrgDetailHeader({
  data,
  session,
}: {
  data: OpsOrgDetail;
  session?: ImpersonationSession | null;
}) {
  const displayName =
    data.customer?.customer_company_name ||
    data.customer?.customer_name ||
    `Organization ${data.organization_id}`;

  return (
    <DetailHeader
      parent={[
        { label: 'Operations', to: '/operations' },
        { label: 'Organizations', to: '/operations/orgs' },
      ]}
      current={displayName}
      idPrefix={`#${data.organization_id}`}
      title={displayName}
      meta={
        <>
          <Chip appearance="subtle" tone="neutral" size="sm">
            {data.services.total} services
          </Chip>
          {data.teams_count > 0 && (
            <Chip appearance="subtle" tone="neutral" size="sm">
              {data.teams_count} teams
            </Chip>
          )}
          {data.projects_count > 0 && (
            <Chip appearance="subtle" tone="neutral" size="sm">
              {data.projects_count} projects
            </Chip>
          )}
          {/* Subtle indicator that CDN/WAAP data is viewed as the customer. */}
          {session && (
            <Chip appearance="subtle" tone="info" size="sm">
              Viewing as customer
            </Chip>
          )}
        </>
      }
    />
  );
}
